import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isEditableTarget } from '@strivelabs/ui-core/lib/isEditableTarget';
import { ANALYTICS_EVENTS, useTrack } from '@/lib/analytics';

/** Where the new-chat action lands. */
const NEW_CHAT_ROUTE = '/ui/chat';

const IS_APPLE =
  typeof navigator !== 'undefined' && /Mac|iP(hone|ad|od)/.test(navigator.userAgent);

/** Label for the shortcut chip, matching the key the platform actually uses. */
export const NEW_CHAT_SHORTCUT = IS_APPLE ? '⌘N' : 'Ctrl+N';

/**
 * Returns a stable callback that starts a new chat. `via` records which
 * affordance the caller is — the button or the keyboard shortcut — so the two
 * can be compared without a second event.
 */
export function useNewChat(via: 'button' | 'shortcut' = 'button') {
  const navigate = useNavigate();
  const trackEvent = useTrack();

  return useCallback(() => {
    trackEvent(ANALYTICS_EVENTS.CHAT_NEW_CHAT_CLICKED, { via });
    navigate(NEW_CHAT_ROUTE);
  }, [navigate, trackEvent, via]);
}

/**
 * Binds the new-chat shortcut for as long as the caller is mounted.
 *
 * Registered at the sidebar root rather than next to the button: the chat tab
 * unmounts when the workspace tab is showing, so a listener owned by the button
 * would only work on the tab that already has the button on screen.
 *
 * The listener ignores keystrokes aimed at a field so it cannot steal input from
 * the composer or a rename dialog. Pass `enabled: false` to withhold the binding
 * from users who may not open chat (guests), for whom it would only round-trip
 * to Reports.
 */
export function useNewChatShortcut({ enabled = true }: { enabled?: boolean } = {}) {
  const startNewChat = useNewChat('shortcut');

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'n') return;
      if (!(event.metaKey || event.ctrlKey) || event.shiftKey || event.altKey) return;
      if (isEditableTarget(event.target)) return;

      event.preventDefault();
      startNewChat();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [startNewChat, enabled]);
}
